"use client";

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { useHotkeys } from "react-hotkeys-hook";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandPanel,
  CommandSearchInput,
  CommandSeparator,
} from "@/components/ui/command";
import { KbdShortcut } from "@/components/ui/kbd-shortcut";
import { type CentralIconName } from "@/types/central-icon";
import { Icon } from "@/components/ui/icon";
import Image from "next/image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { useConnectorLogo } from "@/features/jobs/hooks/use-connector-logo";
import { logo as logoDevUrl, websiteToDomain } from "@/lib/logo-dev";
import { SkeletonBox, SkeletonContainer } from "@/components/ui/skeleton";
import { coverForId } from "@/lib/image/covers";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth";
import {
  useProjectSwitch,
  useProjectSwitchStore,
} from "@/features/projects/hooks/use-project-switch";
import {
  useUniversalSearchResults,
  type UniversalSearchGroupKey,
} from "@/hooks/use-universal-search";
import { NEUROSCALE_LINKS } from "@/lib/neuroscale";
import { useCommandMenu } from "@/stores/command-menu-store";
import { COPY } from "@/features/conversations/lib/copy";
import { CONVERSATIONS_ROUTES } from "@/features/conversations/lib/routes";
import { useConversationsFeature } from "@/features/conversations/hooks/use-conversations-feature";

interface NavItem {
  label: string;
  description: string;
  icon: string;
  /** Solid disc color behind the white icon glyph. */
  iconBg: string;
  href: string;
  keywords?: string[];
  external?: boolean;
  feature?: "conversations";
}

const NAV_SECTIONS: { heading: string; items: NavItem[] }[] = [
  {
    heading: "Navigation",
    items: [
      {
        label: "Talent Search",
        description: "Find and source candidates with AI",
        icon: "IconSearchIntelligence",
        iconBg: "bg-[#de9c50]",
        href: "/talent-search",
        keywords: ["find", "candidates", "source", "search"],
      },
      {
        label: "Projects",
        description: "Browse your project workspaces",
        icon: "IconFolderBookmarks",
        iconBg: "bg-[#4f78e0]",
        href: "/projects",
        keywords: ["project", "folder"],
      },
      {
        label: "Jobs",
        description: "Manage open roles and pipelines",
        icon: "IconBag",
        iconBg: "bg-[#0f9d8f]",
        href: "/jobs",
        keywords: ["job", "position", "role"],
      },
      {
        label: COPY.area,
        description: "Recordings, transcripts, and scheduled meetings",
        icon: "IconVideo",
        iconBg: "bg-[#c2564b]",
        href: CONVERSATIONS_ROUTES.area,
        keywords: ["meeting", "conversation", "recording", "transcript", "interview"],
        feature: "conversations",
      },
      {
        label: "Tasks",
        description: "Your to-dos and checklists",
        icon: "IconSquareChecklist",
        iconBg: "bg-[#7c5cff]",
        href: "/tasks",
        keywords: ["todo", "task", "checklist"],
      },
    ],
  },
  {
    heading: "Engage",
    items: [
      {
        label: "Presets",
        description: "Saved search filters, ready to reuse",
        icon: "IconLayoutDashboard",
        iconBg: "bg-[#d4588f]",
        href: "/presets",
        keywords: ["preset", "filter", "saved"],
      },
      {
        label: "Snippets",
        description: "Reusable text shortcuts for outreach",
        icon: "IconScissors2",
        iconBg: "bg-[#528fc9]",
        href: "/snippets",
        keywords: ["snippet", "text", "shortcut"],
      },
      {
        label: "Team network",
        description: "Your team's LinkedIn connections and intro paths",
        icon: "IconHandshake",
        iconBg: "bg-[#4f9d69]",
        href: "/network",
        keywords: ["network", "connections", "linkedin", "intro"],
      },
    ],
  },
  {
    heading: "Resources",
    items: [
      {
        label: "Settings",
        description: "Account, billing and team preferences",
        icon: "IconSettingsGear2",
        iconBg: "bg-[#8a8f98]",
        href: "/settings",
        keywords: ["settings", "preferences", "account", "billing", "team"],
      },
      {
        label: "Documentation",
        description: "Guides, tips and the help center",
        icon: "IconGraduateCap",
        iconBg: "bg-[#c9903f]",
        href: NEUROSCALE_LINKS.docs,
        keywords: ["documentation", "help", "support"],
        external: true,
      },
    ],
  },
];

function navItemMatches(item: NavItem, query: string): boolean {
  const haystack = `${item.label} ${item.description} ${
    item.keywords?.join(" ") ?? ""
  }`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

const numberFormat = new Intl.NumberFormat("en-US");

const PRESET_TYPE_LABELS: Record<string, string> = {
  companies: "Companies preset",
  universities: "Universities preset",
  job_titles: "Job titles preset",
  skills: "Skills preset",
  investors: "Investors preset",
  locations: "Locations preset",
  filters: "Filters preset",
  job_stages: "Job stages preset",
};

/** Icon disc matching the nav items' visual language. */
function ResultDisc({
  icon,
  iconBg,
}: {
  icon: CentralIconName;
  iconBg: string;
}) {
  return (
    <span
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_1px_2px_rgba(0,0,0,0.15)]",
        iconBg,
      )}
    >
      <Icon name={icon} size={16} className="text-white" />
    </span>
  );
}

function ResultLabel({
  title,
  subtitle,
  titleAccessory,
}: {
  title: string;
  subtitle?: ReactNode;
  titleAccessory?: ReactNode;
}) {
  return (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-sm font-medium text-heading">
          {title}
        </span>
        {titleAccessory}
      </span>
      {subtitle ? (
        <span className="flex min-w-0 items-center gap-1 text-xs text-contrast/45">
          {typeof subtitle === "string" ? (
            <span className="truncate">{subtitle}</span>
          ) : (
            subtitle
          )}
        </span>
      ) : null}
    </span>
  );
}

/** Small ATS connector logo shown next to ATS-linked jobs. */
function AtsConnectorIcon({ connectorId }: { connectorId: string }) {
  const logoSrc = useConnectorLogo(connectorId);
  if (!logoSrc) return null;
  return (
    <Image
      src={logoSrc}
      alt=""
      width={14}
      height={14}
      className="size-3.5 shrink-0 rounded-[3px] object-cover"
    />
  );
}

function RecencyBadge({ recency }: { recency: "added" | "viewed" }) {
  return (
    <Badge
      variant={recency === "added" ? "success" : "info"}
      size="xs"
      className="shrink-0"
    >
      {recency === "added" ? "Recently added" : "Recently viewed"}
    </Badge>
  );
}

/**
 * Entity-frame skeleton — mirrors a result row (avatar disc + title +
 * subtitle) with the shared shimmer so the sweep is coordinated across rows.
 * Insets mirror a real group (CommandGroup py-2 + heading pt-2.5/pb-1.5), so
 * the skeleton swaps 1:1 with the rendered results.
 */
function ResultsSkeleton() {
  return (
    <SkeletonContainer className="flex-col px-3 pt-2 pb-2">
      {[0, 1].map((section) => (
        <div key={section} className="flex flex-col">
          <SkeletonBox
            className={cn(
              "mx-3 mb-1.5 h-3 rounded",
              section === 0 ? "mt-2.5 w-20" : "mt-6 w-24",
            )}
            tone="muted"
          />
          {(section === 0 ? [0, 1, 2] : [0, 1]).map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-3 py-2"
            >
              <SkeletonBox className="size-8 shrink-0 rounded-full" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <SkeletonBox
                  className="h-3.5 rounded"
                  style={{ width: `${52 - i * 9 - section * 4}%` }}
                />
                <SkeletonBox
                  className="h-3 rounded"
                  tone="muted"
                  style={{ width: `${34 - i * 5 + section * 6}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ))}
    </SkeletonContainer>
  );
}

/** Rows-only skeleton used while a group's "See more" expansion loads. */
function SkeletonEntityRows({ count }: { count: number }) {
  return (
    <SkeletonContainer className="flex-col">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2">
          <SkeletonBox className="size-8 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <SkeletonBox
              className="h-3.5 rounded"
              style={{ width: `${48 - i * 9}%` }}
            />
            <SkeletonBox
              className="h-3 rounded"
              tone="muted"
              style={{ width: `${32 - i * 5}%` }}
            />
          </div>
        </div>
      ))}
    </SkeletonContainer>
  );
}

interface EntityRow {
  key: string;
  leading: ReactNode;
  title: string;
  subtitle?: ReactNode;
  titleAccessory?: ReactNode;
  onSelect: () => void;
}

interface EntityGroup {
  key: UniversalSearchGroupKey;
  heading: string;
  rows: EntityRow[];
  hasMore: boolean;
}

export function SearchMenu() {
  const router = useRouter();
  // Shared with the sidebar's "Search ⌘K" row — one dialog, many triggers.
  const { open, setOpen } = useCommandMenu();
  const [search, setSearchState] = useState("");
  const [expandedTypes, setExpandedTypes] = useState<
    ReadonlySet<UniversalSearchGroupKey>
  >(new Set());
  const { selectedProjectId, isLoading: authLoading } = useAuth();
  const requireProjectForJobsAndSearch = !authLoading && !selectedProjectId;
  const conversationsEnabled = useConversationsFeature();

  const trimmedSearch = search.trim();
  const expandedList = useMemo(() => [...expandedTypes], [expandedTypes]);
  const { data, hasAnyResults, isSearching, isExpanding } =
    useUniversalSearchResults(search, open, expandedList);

  const setSearch = useCallback((value: string) => {
    setSearchState(value);
    // A new query invalidates any per-group expansion.
    setExpandedTypes((prev) => (prev.size ? new Set() : prev));
  }, []);

  // Where to land once a cross-project switch completes.
  const pendingHrefRef = useRef<string | null>(null);
  const { switchProject } = useProjectSwitch({
    onSuccess: () => {
      const href = pendingHrefRef.current;
      pendingHrefRef.current = null;
      router.push(href ?? "/jobs");
    },
  });

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) setSearch("");
    },
    [setOpen, setSearch],
  );

  const runCommand = useCallback(
    (command: () => void) => {
      handleOpenChange(false);
      command();
    },
    [handleOpenChange],
  );

  /**
   * Navigate to an entity that lives in `projectId`. Entities in the active
   * project navigate directly; anything else switches projects first (with
   * the full-screen loader, mirroring the navbar project picker) and lands
   * on the entity.
   */
  const openInProject = useCallback(
    (
      projectId: string,
      href: string,
      optimistic?: {
        id: string;
        title: string;
        cover_image_url?: string | null;
      },
    ) => {
      if (projectId === selectedProjectId) {
        router.push(href);
        return;
      }
      pendingHrefRef.current = href;
      useProjectSwitchStore.setState({ showLoader: true });
      switchProject(
        projectId,
        optimistic
          ? {
              id: optimistic.id,
              title: optimistic.title,
              cover_image_url: optimistic.cover_image_url ?? null,
            }
          : undefined,
      );
    },
    [router, selectedProjectId, switchProject],
  );

  useHotkeys(
    "mod+k",
    () => {
      handleOpenChange(!open);
    },
    {
      preventDefault: true,
      enableOnFormTags: true,
    },
    [open, handleOpenChange],
  );

  const filteredNavSections = useMemo(() => {
    const gated = NAV_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => conversationsEnabled || item.feature !== "conversations",
      ),
    })).filter((section) => section.items.length > 0);
    if (!trimmedSearch) return gated;
    return gated
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          navItemMatches(item, trimmedSearch),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [trimmedSearch, conversationsEnabled]);

  const entityGroups = useMemo<EntityGroup[]>(() => {
    if (!data) return [];
    const groups: EntityGroup[] = [
      {
        key: "projects",
        heading: "Projects",
        hasMore: data.projects.has_more,
        rows: data.projects.items.map((project) => ({
          key: `project:${project.id}`,
          leading: (
            <Avatar className="size-8 shrink-0 rounded-full border border-contrast/8">
              <AvatarImage src={project.cover_image_url ?? undefined} alt="" />
              <AvatarFallback className="p-0 bg-transparent">
                <img
                  src={coverForId(project.id)}
                  alt=""
                  className="size-full object-cover"
                />
              </AvatarFallback>
            </Avatar>
          ),
          title: project.title,
          subtitle:
            project.id === selectedProjectId
              ? "Current project"
              : "Set as active project",
          onSelect: () =>
            openInProject(project.id, "/jobs", {
              id: project.id,
              title: project.title,
              cover_image_url: project.cover_image_url,
            }),
        })),
      },
      {
        key: "jobs",
        heading: "Jobs",
        hasMore: data.jobs.has_more,
        rows: data.jobs.items.map((job) => ({
          key: `job:${job.id}`,
          leading: (
            <Avatar className="size-8 shrink-0 rounded-full border border-contrast/8">
              <AvatarImage
                src={
                  logoDevUrl({
                    domain: job.employer_website
                      ? websiteToDomain(job.employer_website)
                      : null,
                    name: job.employer_name,
                    size: 64,
                  }) ?? undefined
                }
                alt=""
              />
              <AvatarFallback className="p-0 bg-transparent">
                <ResultDisc icon="IconBag" iconBg="bg-[#0f9d8f]" />
              </AvatarFallback>
            </Avatar>
          ),
          title: job.title,
          titleAccessory: job.ats_connector_id ? (
            <AtsConnectorIcon connectorId={job.ats_connector_id} />
          ) : undefined,
          subtitle: `${job.project_title} · ${numberFormat.format(
            job.candidate_count,
          )} ${job.candidate_count === 1 ? "candidate" : "candidates"}`,
          onSelect: () =>
            openInProject(job.project_id, `/jobs/${job.id}/pipeline`),
        })),
      },
      {
        key: "candidates",
        heading: "Candidates",
        hasMore: data.candidates.has_more,
        rows: data.candidates.items.map((candidate) => ({
          key: `candidate:${candidate.job_id}:${candidate.profile_id}`,
          leading: (
            <CandidateAvatar
              name={candidate.full_name}
              avatarUrl={candidate.avatar_url}
              size={32}
              initialsPx={12}
            />
          ),
          title: candidate.full_name,
          titleAccessory: candidate.recency ? (
            <RecencyBadge recency={candidate.recency} />
          ) : undefined,
          subtitle: (
            <>
              {candidate.headline ? (
                <>
                  <span className="truncate">{candidate.headline}</span>
                  <span className="shrink-0">·</span>
                </>
              ) : null}
              <Icon
                name="IconBag"
                size={11}
                className="shrink-0 text-contrast opacity-40"
              />
              <span className="truncate">{candidate.job_title}</span>
            </>
          ),
          onSelect: () =>
            openInProject(
              candidate.project_id,
              `/jobs/${candidate.job_id}/pipeline?profile=${candidate.profile_id}`,
            ),
        })),
      },
      {
        key: "sequences",
        heading: "Sequences",
        hasMore: data.sequences.has_more,
        rows: data.sequences.items.map((sequence) => ({
          key: `sequence:${sequence.id}`,
          leading: <ResultDisc icon="IconPaperPlane" iconBg="bg-[#7c5cff]" />,
          title: sequence.name,
          subtitle: sequence.job_title,
          onSelect: () =>
            openInProject(
              sequence.project_id,
              `/jobs/${sequence.job_id}/sequences/${sequence.id}/enrollments`,
            ),
        })),
      },
      {
        key: "searches",
        heading: "Talent Searches",
        hasMore: data.searches.has_more,
        rows: data.searches.items.map((savedSearch) => ({
          key: `search:${savedSearch.id}`,
          leading: (
            <ResultDisc icon="IconSearchIntelligence" iconBg="bg-[#de9c50]" />
          ),
          title: savedSearch.name,
          subtitle: [
            savedSearch.project_title,
            savedSearch.result_count != null
              ? `${numberFormat.format(savedSearch.result_count)} results`
              : null,
          ]
            .filter(Boolean)
            .join(" · "),
          onSelect: () =>
            openInProject(
              savedSearch.project_id,
              `/talent-search/${savedSearch.id}`,
            ),
        })),
      },
      {
        key: "tasks",
        heading: "Tasks",
        hasMore: data.tasks.has_more,
        rows: data.tasks.items.map((task) => ({
          key: `task:${task.id}`,
          leading: (
            <ResultDisc icon="IconSquareChecklist" iconBg="bg-[#7c5cff]" />
          ),
          title: task.title,
          subtitle: task.job_title,
          onSelect: () =>
            openInProject(task.project_id, `/tasks?task=${task.id}`),
        })),
      },
      {
        key: "snippets",
        heading: "Snippets",
        hasMore: data.snippets.has_more,
        rows: data.snippets.items.map((snippet) => ({
          key: `snippet:${snippet.id}`,
          leading: <ResultDisc icon="IconScissors2" iconBg="bg-[#528fc9]" />,
          title: snippet.name,
          subtitle:
            snippet.visibility === "private"
              ? "Private snippet"
              : "Shared snippet",
          // Snippets are org-level — no project switch needed.
          onSelect: () => router.push(`/snippets?edit=${snippet.id}`),
        })),
      },
      {
        key: "presets",
        heading: "Presets",
        hasMore: data.presets.has_more,
        rows: data.presets.items.map((preset) => ({
          key: `preset:${preset.id}`,
          leading: (
            <ResultDisc icon="IconLayoutDashboard" iconBg="bg-[#d4588f]" />
          ),
          title: preset.title,
          subtitle:
            PRESET_TYPE_LABELS[preset.preset_type] ?? preset.preset_type,
          // Presets are org-level — no project switch needed.
          onSelect: () => router.push(`/presets/${preset.preset_type}`),
        })),
      },
    ];
    return groups.filter((group) => group.rows.length > 0);
  }, [data, openInProject, router, selectedProjectId]);

  const showSkeleton = isSearching && !data;
  const noResults =
    filteredNavSections.length === 0 &&
    !hasAnyResults &&
    !isSearching &&
    trimmedSearch.length > 0;

  // Ordered list of visible groups so each group after the first draws a
  // separator, regardless of which groups have results.
  const visibleGroupKeys = useMemo(() => {
    const keys: string[] = entityGroups.map((group) => group.key);
    for (const section of filteredNavSections) {
      keys.push(`nav:${section.heading}`);
    }
    return keys;
  }, [entityGroups, filteredNavSections]);

  const needsSeparator = (key: string) => visibleGroupKeys.indexOf(key) > 0;

  // No inline trigger: the palette is opened from the sidebar "Search" row
  // or the ⌘K hotkey (both go through the shared command-menu store).
  return (
    <>
      <CommandDialog
        open={open}
        onOpenChange={handleOpenChange}
        title="Command Menu"
        description="Search and navigate across Arbi"
        showCloseButton={false}
        shouldFilter={false}
      >
        <CommandSearchInput
          placeholder="Search projects, jobs, candidates, sequences..."
          value={search}
          onValueChange={setSearch}
        />
        <CommandPanel>
          <CommandList>
            {noResults && (
              <CommandEmpty>
                <div className="flex flex-col items-center gap-2 py-2">
                  <Icon
                    name="IconSearchIntelligence"
                    size={20}
                    className="text-heading opacity-20"
                  />
                  <span className="text-contrast/40">No results found</span>
                </div>
              </CommandEmpty>
            )}
            {showSkeleton && <ResultsSkeleton />}

            {entityGroups.map((group) => {
              const expanded = expandedTypes.has(group.key);
              return (
                <div key={group.key}>
                  {needsSeparator(group.key) && <CommandSeparator />}
                  <CommandGroup heading={group.heading}>
                    {group.rows.map((row) => (
                      <CommandItem
                        key={row.key}
                        value={row.key}
                        className="group/item gap-3 px-3 py-2"
                        onSelect={() => runCommand(row.onSelect)}
                      >
                        {row.leading}
                        <ResultLabel
                          title={row.title}
                          subtitle={row.subtitle}
                          titleAccessory={row.titleAccessory}
                        />
                      </CommandItem>
                    ))}
                    {group.hasMore && !expanded && (
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedTypes(
                            (prev) => new Set([...prev, group.key]),
                          )
                        }
                        className="flex w-fit cursor-pointer items-center gap-1 px-3 py-1.5 text-xs text-contrast/45 transition-colors hover:text-heading"
                      >
                        See more {group.heading.toLowerCase()}
                        <Icon name="IconArrowRight" size={11} />
                      </button>
                    )}
                    {expanded && isExpanding && (
                      <SkeletonEntityRows count={2} />
                    )}
                    {expanded && !isExpanding && group.hasMore && (
                      <div className="px-3 py-1.5 text-xs text-contrast/35">
                        Showing the top {group.rows.length} · refine your
                        search to narrow further
                      </div>
                    )}
                  </CommandGroup>
                </div>
              );
            })}

            {filteredNavSections.map((section) => (
              <div key={section.heading}>
                {needsSeparator(`nav:${section.heading}`) && (
                  <CommandSeparator />
                )}
                <CommandGroup heading={section.heading}>
                  {section.items.map((item) => (
                    <CommandItem
                      key={item.href}
                      value={`nav:${item.href}`}
                      className="group/item gap-3 px-3 py-2"
                      disabled={
                        requireProjectForJobsAndSearch &&
                        (item.href === "/jobs" ||
                          item.href === "/talent-search")
                      }
                      onSelect={() =>
                        runCommand(() => {
                          if (item.external) {
                            window.open(item.href, "_blank");
                          } else {
                            router.push(item.href);
                          }
                        })
                      }
                    >
                      <ResultDisc
                        icon={item.icon as CentralIconName}
                        iconBg={item.iconBg}
                      />
                      <ResultLabel
                        title={item.label}
                        subtitle={item.description}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            ))}
          </CommandList>
          <div className="flex items-center justify-between border-t border-contrast/6 bg-surface-header px-3.5 py-2">
            <div className="flex items-center gap-3 text-xs text-contrast/35">
              <span className="flex items-center gap-1.5">
                <KbdShortcut keys={["↑", "↓"]} />
                navigate
              </span>
              <span className="flex items-center gap-1.5">
                <KbdShortcut keys={["↵"]} />
                select
              </span>
            </div>
            <span className="flex items-center gap-1.5 text-xs text-contrast/35">
              <KbdShortcut keys={["mod", "K"]} />
              to open anywhere
            </span>
          </div>
        </CommandPanel>
      </CommandDialog>
    </>
  );
}
